import { ListStateProvider } from "../lib/listState";
import { listRouteKeys } from "../lib/listRoutes";
import { workspaceExperience } from "../lib/workspaceExperience";
import {
  Boxes,
  Building2,
  CalendarDays,
  Check,
  CheckSquare,
  ClipboardList,
  ChevronDown,
  ChevronRight,
  Folder,
  GitFork,
  Factory,
  Home,
  Inbox,
  LineChart,
  Loader2,
  LogOut,
  MapPin,
  Menu,
  PackageSearch,
  Plus,
  Settings,
  Timer,
  Truck,
  Users
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { type ReactNode, Suspense, useEffect, useId, useRef, useState } from "react";
import { Link, Outlet, useLocation, useMatch, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Logo } from "./Logo";
import { api, ApiError } from "../lib/api";
import { useSession } from "../lib/session";
import { tabAccess } from "../lib/tabAccess";
import { isInboxOnlyWebUser } from "../features/auth/flow";
import type { OrganizationTabCode, UserContextResponse } from "../lib/types";

type NavItem = {
  group?: string;
  to: string;
  label: string;
  icon: LucideIcon;
  disabled?: boolean;
  printerOnly?: boolean;
  taskWorkspaceOnly?: boolean;
  agencyOnly?: boolean;
  brandOnly?: boolean;
  settingsWorkspaceOnly?: boolean;
  productionOnly?: boolean;
  productionManagerOnly?: boolean;
  leaveOnly?: boolean;
  tabCode?: OrganizationTabCode;
  count?: number;
};

const navItems: NavItem[] = [
  { to: "/", label: "Home", icon: Home },
  { to: "/inbox", label: "Inbox", icon: Inbox },
  { group: "Client work", to: "/projects", label: "Projects", icon: Folder },
  { group: "Client work", to: "/tasks", label: "Tasks", icon: CheckSquare },
  { group: "Client work", to: "/production", label: "Production", icon: Factory },
  { group: "Client work", to: "/delivery", label: "Delivery", icon: Truck, productionManagerOnly: true },
  { group: "My Work", to: "/my-work/projects", label: "Projects", icon: Folder },
  { group: "Procurement", to: "/procurement/materials", label: "Materials", icon: PackageSearch },
  { group: "Procurement", to: "/procurement/inventory", label: "Inventory", icon: Boxes },
  { group: "Procurement", to: "/procurement/suppliers", label: "Suppliers", icon: Building2 },
  { group: "Procurement", to: "/procurement/orders", label: "Orders", icon: ClipboardList },
  { group: "Network", to: "/customers", label: "Customers", icon: Users, tabCode: "CRM" },
  { group: "Network", to: "/vendors", label: "Vendors", icon: Building2, tabCode: "CRM" },
  { group: "Network", to: "/catalogue", label: "Catalogue", icon: PackageSearch },
  { group: "Network", to: "/rate-cards", label: "Rate Cards", icon: CalendarDays, tabCode: "RATE_CARDS" },
  { group: "Staff", to: "/users", label: "Users", icon: Users, tabCode: "USERS" },
  { group: "Staff", to: "/staff-reporting", label: "Reporting", icon: GitFork, tabCode: "USERS" },
  { group: "Staff", to: "/leave-requests", label: "Leave", icon: CalendarDays, leaveOnly: true },
  { group: "Staff", to: "/duty-status", label: "Duty", icon: Timer, tabCode: "DUTY_STATUS" },
  { group: "Organization", to: "/locations", label: "Locations", icon: MapPin },
  { group: "Organization", to: "/organization/addresses", label: "Addresses", icon: MapPin, tabCode: "SETTINGS" },
  { group: "Organization", to: "/organization/profile", label: "Profile", icon: Building2, tabCode: "SETTINGS" },
  { group: "Organization", to: "/assets", label: "Assets", icon: Boxes },
  { to: "/settings", label: "Settings", icon: Settings, tabCode: "SETTINGS" }
];

function labelFromCode(value: string | null | undefined) {
  if (!value) {
    return "Workspace";
  }
  return value
    .toLowerCase()
    .split("_")
    .map((part) => part.slice(0, 1).toUpperCase() + part.slice(1))
    .join(" ");
}

function ContentLoading() {
  return (
    <div className="content-route-loading" aria-live="polite" aria-label="Loading page">
      <div className="skeleton-line wide" />
      <div className="skeleton-grid">
        <div />
        <div />
        <div />
      </div>
    </div>
  );
}

function workspaceErrorMessage(error: unknown) {
  return error instanceof ApiError ? error.message : "The organization could not be updated.";
}

function NavigationGroup({ label, expanded, onToggle, children }: {
  label: string;
  expanded: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  const panelId = useId();
  return (
    <div className={`navigation-group${expanded ? " is-expanded" : ""}`}>
      <button
        type="button"
        className="navigation-group-toggle"
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={onToggle}
      >
        {label}
        <span className="navigation-group-chevron" aria-hidden="true"><ChevronDown size={14} /></span>
      </button>
      <div id={panelId} className="navigation-group-panel" inert={!expanded} aria-hidden={!expanded}>
        <div className="navigation-group-items">{children}</div>
      </div>
    </div>
  );
}

export function AppShell() {
  const workspaceMenuRef = useRef<HTMLDetailsElement>(null);
  const navigationMenuRef = useRef<HTMLDetailsElement>(null);
  const location = useLocation();
  const [expandedNavigationGroup, setExpandedNavigationGroup] = useState<string | null>(() =>
    navItems.find(item => item.group && (location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)))?.group ?? null
  );
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useSession();
  const userContextQuery = useQuery({
    queryKey: ["user-context"],
    queryFn: api.getUserContext
  });

  const user = userContextQuery.data?.user;
  const organization = userContextQuery.data?.active_organization;
  const accountName = user?.name?.trim() || "Coprint User";
  const organizationNameLabel = organization?.organization_name ?? "Coprint";
  const availableOrganizations = userContextQuery.data?.available_organizations
    ?? (organization ? [organization] : []);
  const settingsAccess = tabAccess(userContextQuery.data, "SETTINGS");
  const leaveOptionsQuery = useQuery({ queryKey: ["leave-options", organization?.organization_id], queryFn: api.getLeaveOptions, enabled: Boolean(organization) });
  const rateCardsAccess = tabAccess(userContextQuery.data, "RATE_CARDS");
  const isInboxOnlyUser = isInboxOnlyWebUser(userContextQuery.data?.context_status);
  const experience = workspaceExperience(userContextQuery.data);
  const isProductionTeamUser = !experience && user?.registration_type === "PRODUCTION_TEAM";
  const usesCompactInboxShell = isInboxOnlyUser;
  const canCreateProjects = Boolean(organization) && tabAccess(userContextQuery.data, "CRM").can_update;
  const isCreateProjectPage = location.pathname.endsWith("/projects/create");
  const isProjectsPage = location.pathname === "/projects" || location.pathname === "/my-work/projects";
  const projectDetailsMatch = useMatch("/projects/:projectId");
  const projectId = isCreateProjectPage ? undefined : projectDetailsMatch?.params.projectId;
  const agencyInboxQuery = useQuery({
    queryKey: ["inbox-tasks", organization?.organization_id, Boolean(experience)],
    queryFn: () => experience ? api.listWorkspaceInbox() : api.listInboxTasks({ includeCompleted: false }),
    enabled: Boolean(userContextQuery.data) && !isProductionTeamUser
  });
  const productionWorkspaceQuery = useQuery({
    queryKey: ["production-workspace"],
    queryFn: () => api.getProductionWorkspace(),
    enabled: isProductionTeamUser
  });
  const isProductionManagerUser = isProductionTeamUser && Boolean(productionWorkspaceQuery.data?.can_manage);
  const projectDetailsQuery = useQuery({
    // Share the detail page's cache so status changes update the breadcrumb too.
    queryKey: ["project-details", projectId],
    queryFn: () => api.getProjectDetails(projectId!),
    enabled: Boolean(projectId) && !isInboxOnlyUser && (!isProductionTeamUser || isProductionManagerUser),
    staleTime: 10_000
  });
  const project = projectId ? projectDetailsQuery.data?.project_details.project : undefined;
  const projectDisplayStatus = project?.display_status ?? project?.status;
  const organizationId = organization?.organization_id;
  const isProjectExecutor = (project?.printer?.business_id ?? project?.vendor?.business_id) === organizationId;
  const isMyWorkProject = location.pathname.startsWith("/my-work/") || Boolean(project && organizationId && !isProjectExecutor && (
    project.agency?.business_id === organizationId || project.brand_owner?.business_id === organizationId
  ));
  const projectListPath = isMyWorkProject ? "/my-work/projects" : "/projects";
  const activeNavigationPath = projectId && project ? projectListPath : location.pathname;
  const activeNavigationGroup = navItems.find(item => item.group && (
    activeNavigationPath === item.to || activeNavigationPath.startsWith(`${item.to}/`)
  ))?.group ?? null;
  useEffect(() => {
    setExpandedNavigationGroup(activeNavigationGroup);
  }, [activeNavigationGroup, activeNavigationPath]);
  const breadcrumbTitle = isCreateProjectPage ? "Create Project" : projectId ? project?.project_name ?? "Project" : "Projects";

  function applyWorkspaceContext(context: UserContextResponse) {
    workspaceMenuRef.current?.removeAttribute("open");
    setExpandedNavigationGroup(null);
    queryClient.clear();
    queryClient.setQueryData(["user-context"], context);
    navigate("/", { replace: true });
  }

  const switchOrganizationMutation = useMutation({
    mutationFn: api.switchActiveOrganization,
    onSuccess: applyWorkspaceContext
  });

  useEffect(() => {
    function closeWorkspaceMenu(event: PointerEvent) {
      const menu = workspaceMenuRef.current;
      if (menu?.open && event.target instanceof Node && !menu.contains(event.target)) {
        menu.removeAttribute("open");
      }
      const navigationMenu = navigationMenuRef.current;
      if (navigationMenu?.open && event.target instanceof Node && !navigationMenu.contains(event.target)) {
        navigationMenu.removeAttribute("open");
      }
    }

    function closeMenusOnEscape(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      for (const menu of [workspaceMenuRef.current, navigationMenuRef.current]) {
        if (menu?.open) {
          menu.removeAttribute("open");
          menu.querySelector("summary")?.focus();
        }
      }
    }

    document.addEventListener("pointerdown", closeWorkspaceMenu);
    document.addEventListener("keydown", closeMenusOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeWorkspaceMenu);
      document.removeEventListener("keydown", closeMenusOnEscape);
    };
  }, []);

  useEffect(() => {
    navigationMenuRef.current?.removeAttribute("open");
    workspaceMenuRef.current?.removeAttribute("open");
  }, [location.pathname, location.search]);

  function signOut() {
    session.clearSession();
    queryClient.clear();
    navigate("/login", { replace: true });
  }

  const renderedItems = navItems.map((item) => {
    if (experience && !experience.leadership) {
      if (["/projects", "/my-work/projects", "/delivery"].includes(item.to) || item.group === "Procurement") return null;
      if (item.to === "/production" && !userContextQuery.data?.workspace?.assigned_task_types.some(type => type === "PRODUCTION" || type === "PACKAGING")) return null;
    }
    if (item.leaveOnly && !leaveOptionsQuery.data?.enabled) return null;
    const tabPermission = item.tabCode ? tabAccess(userContextQuery.data, item.tabCode) : null;
    if ((item.tabCode === "SETTINGS" || item.tabCode === "DUTY_STATUS") && !tabPermission?.can_view) {
      return null;
    }
    const Icon = item.icon;
    const itemCount = item.to === "/inbox"
      ? agencyInboxQuery.data?.length
      : "count" in item ? item.count : undefined;
    const disabled = item.disabled || (tabPermission ? !tabPermission.can_view : false);
    if (disabled) {
      return (
        <span key={item.to} className="nav-item disabled">
          <Icon size={18} />
          <span>{item.label}</span>
          {itemCount ? <span className="nav-count">{itemCount}</span> : null}
        </span>
      );
    }
    const active = activeNavigationPath === item.to || (item.to !== "/" && activeNavigationPath.startsWith(`${item.to}/`));
    return (
      <Link key={item.to} to={item.to} className={`nav-item${active ? " active" : ""}`} aria-current={active ? "page" : undefined}>
        <Icon size={18} />
        <span>{item.label}</span>
        {itemCount ? <span className="nav-count">{itemCount}</span> : null}
      </Link>
    );
  });

  const navigation = <>
    {renderedItems.filter((_, index) => !navItems[index].group && navItems[index].to !== "/settings")}
    {["Client work", "My Work", "Procurement", "Network", "Staff", "Organization"].map(group => {
      const children = renderedItems.filter((element, index) => element && navItems[index].group === group);
      return children.length ? (
        <NavigationGroup
          key={group}
          label={group}
          expanded={expandedNavigationGroup === group}
          onToggle={() => setExpandedNavigationGroup(current => current === group ? null : group)}
        >
          {children}
        </NavigationGroup>
      ) : null;
    })}
    <div className="navigation-settings">{renderedItems[navItems.findIndex(item => item.to === "/settings")]}</div>
  </>;

  return (
    <>
      <div className={`app-shell${usesCompactInboxShell ? " inbox-only-shell" : ""}`}>
      <aside className="sidebar">
        <Logo />
        <nav className="sidebar-nav" aria-label="Primary navigation">
          {navigation}
        </nav>
        {!isInboxOnlyUser ? (
          <div className="quick-create">
            <div className="quick-create-title">Quick Create</div>
            <button
              type="button"
              onClick={() => navigate("/projects/create")}
              disabled={!canCreateProjects}
              title={canCreateProjects ? "Create a project" : "Project creation is available to project-owner workspaces."}
            >
              <Plus size={16} /> New Project
            </button>
            <button
              type="button"
              onClick={() => navigate("/rate-cards/create")}
              disabled={!rateCardsAccess.can_update}
              title={rateCardsAccess.can_update ? "Create a rate card" : "You have read-only access to Rate Cards."}
            >
              <Plus size={16} /> Rate Card
            </button>
            <button type="button" disabled><Plus size={16} /> Upload Files</button>
          </div>
        ) : null}
      </aside>
      <div className="main-frame">
        <header className="topbar">
          <details ref={navigationMenuRef} className="mobile-navigation-menu">
            <summary aria-label="Open navigation" title="Open navigation" aria-controls="mobile-primary-navigation">
              <Menu size={21} aria-hidden="true" />
            </summary>
            <nav id="mobile-primary-navigation" className="mobile-navigation-drawer sidebar-nav" aria-label="Mobile primary navigation">
              {navigation}
            </nav>
          </details>
          <div className="mobile-shell-logo"><Logo /></div>
          {isCreateProjectPage || isProjectsPage || projectId ? (
            <nav className={`topbar-context${projectId ? " topbar-project-context" : ""}`} aria-label="Breadcrumb">
              <ol>
                <li>
                  <Link to={projectListPath}>{isMyWorkProject ? "My Work" : "Client work"}</Link>
                  <ChevronRight size={14} aria-hidden="true" />
                </li>
                {isCreateProjectPage || projectId ? <li>
                  <Link to={projectListPath}>Projects</Link>
                  <ChevronRight size={14} aria-hidden="true" />
                </li> : null}
                <li className={projectId ? "topbar-project-current" : undefined}>
                  <h1><Link to={{ pathname: location.pathname, search: location.search }} aria-current="page" title={project?.project_name}>{breadcrumbTitle}</Link></h1>
                  {projectDisplayStatus ? <span className={`project-status-pill status-${projectDisplayStatus.toLowerCase()}`}>{projectDisplayStatus === "SENT" ? "Waiting for vendor acceptance" : labelFromCode(projectDisplayStatus)}</span> : null}
                </li>
              </ol>
            </nav>
          ) : null}
          {location.pathname === "/tasks" ? <div className="topbar-page-heading">
            <h1>Tasks</h1>
            <p>Plan daily work and assign tasks across your team.</p>
          </div> : null}
          <details ref={workspaceMenuRef} className="workspace-menu">
            <summary className="workspace-switcher" aria-label={`Account menu: ${accountName}, ${organizationNameLabel}`}>
              <span className="workspace-account-avatar" aria-hidden="true">{accountName.slice(0, 1).toUpperCase()}</span>
              <span>
                <strong title={accountName}>{accountName}</strong>
                <small title={organizationNameLabel}>{organizationNameLabel}</small>
              </span>
              <ChevronDown size={16} aria-hidden="true" />
            </summary>
            <div className="workspace-menu-drawer">
              <div className="workspace-account-details">
                <strong>{accountName}</strong>
                <span>{organizationNameLabel}</span>
              </div>
              {availableOrganizations.length > 1 ? <>
              <div className="workspace-menu-label">Switch organization</div>
              <div className="workspace-menu-list" role="group" aria-label="Switch organization">
                {availableOrganizations.map((availableOrganization) => {
                  const isActive = availableOrganization.organization_id === organization?.organization_id;
                  const isSwitching = switchOrganizationMutation.isPending
                    && switchOrganizationMutation.variables === availableOrganization.organization_id;
                  return (
                    <button
                      key={availableOrganization.organization_id}
                      type="button"
                      className={`workspace-menu-organization${isActive ? " active" : ""}`}
                      aria-current={isActive ? "true" : undefined}
                      disabled={isActive || switchOrganizationMutation.isPending}
                      onClick={() => switchOrganizationMutation.mutate(availableOrganization.organization_id)}
                    >
                      <Building2 size={17} aria-hidden="true" />
                      <span>
                        <strong>{availableOrganization.organization_name}</strong>
                      </span>
                      {isSwitching
                        ? <Loader2 className="spin-icon" size={16} aria-hidden="true" />
                        : isActive ? <Check size={16} aria-hidden="true" /> : null}
                    </button>
                  );
                })}
              </div>
              </> : null}
              {switchOrganizationMutation.isError ? (
                <div className="workspace-menu-error" role="alert">
                  {workspaceErrorMessage(switchOrganizationMutation.error)}
                </div>
              ) : null}
              {settingsAccess.can_view ? (
                <Link to="/organization/profile" className="workspace-menu-command">
                  <Building2 size={16} aria-hidden="true" /> Organization profile
                </Link>
              ) : null}
              <div className="workspace-menu-divider" />
              {userContextQuery.data?.personal_access ? <>
                <Link to="/personal-inbox" className="workspace-menu-command">Personal approvals</Link>
              </> : null}
              <button type="button" className="workspace-menu-command" onClick={signOut}>
                <LogOut size={16} /> Logout
              </button>
            </div>
          </details>
        </header>
        <main className="content">
          <Suspense fallback={<ContentLoading />}>
            {user && <ListStateProvider key={`${user.user_id}:${organization?.organization_id ?? "personal"}`} scope={`${user.user_id}:${organization?.organization_id ?? "personal"}`} routeKeys={listRouteKeys}>
              <Outlet />
            </ListStateProvider>}
          </Suspense>
        </main>
        </div>
      </div>
    </>
  );
}
